<template>
  <div>
    <!-- Sign-out confirmation -->
    <AlertDialog :open="showConfirmModal" @update:open="
      (v) => {
        if (!v) closeModal()
      }
    ">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Sign out?</AlertDialogTitle>
          <AlertDialogDescription>Press confirm to sign out</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel @click="closeModal()">Cancel</AlertDialogCancel>
          <AlertDialogAction @click="signout()">Confirm</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <SidebarShell :collapsed="effectiveCollapsed" :mobile-open="showLinks">
      <!-- Branding -->
      <template #brand>
        <div @click="$router.push('/')" class="z-30 cursor-pointer flex items-center gap-2 min-w-0 flex-1"
          :class="sidebar.collapsed ? 'lg:flex-none lg:justify-center' : ''">
          <img src="../../assets/boogi-logo-gray-800.svg" class="h-7 shrink-0 block dark:hidden"
            :class="sidebar.collapsed ? 'lg:hidden' : ''" />
          <img src="../../assets/boogiwhitelogo.svg" class="h-7 shrink-0 hidden dark:block"
            :class="sidebar.collapsed ? 'lg:hidden dark:lg:hidden' : ''" />
          <!-- Collapsed sidebar shows just the Boogi mark, matched to the theme. -->
          <img v-if="sidebar.collapsed" src="../../assets/boogidashboardicon.png" alt="Boogi"
            class="hidden lg:block dark:lg:hidden h-8 w-8 rounded-md shrink-0" />
          <img v-if="sidebar.collapsed" src="../../assets/boogidashboardicon_darkmode.png" alt="Boogi"
            class="hidden dark:lg:block h-8 w-8 rounded-md shrink-0" />
        </div>
      </template>

      <!-- Header actions: desktop notification bell + mobile hamburger -->
      <template #header-actions>
        <Popover :open="showNotificationsSidebar" @update:open="
          (v) => {
            if (v) {
              showNotificationsSidebar = true
              getNotifications(0)
            } else {
              closeNotifications()
            }
          }
        ">
          <PopoverTrigger as-child>
            <button type="button" class="hidden items-center relative cursor-pointer p-1 rounded hover:bg-muted"
              :class="sidebar.collapsed ? 'lg:hidden' : 'lg:flex'">
              <Bell class="w-6 h-6 text-foreground" :stroke-width="2" />
              <span v-if="notificationCount > 0"
                class="absolute -top-0.5 -right-1 bg-red-600 text-white text-[10px] font-bold rounded-full px-1.5 flex items-center justify-center text-center whitespace-nowrap leading-4">
                {{ notificationCount > 99 ? '99+' : notificationCount }}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" side="right" :side-offset="10" :align-offset="16" class="w-96 p-0">
            <PopoverArrow :width="14" :height="7" />
            <NotificationsPanel :notifications="notifications" :loading="loadingNotifications"
              :historical="historicalNotifications" :last-page="lastPage" @open="openNotification"
              @load-more="loadMoreNotifications" @toggle-historical="toggleHistorical" />
          </PopoverContent>
        </Popover>

        <button type="button" @click="expandDropdown" :class="showLinks ? 'rotate-90' : ''"
          class="cursor-pointer lg:hidden transition-transform duration-200 transform flex items-center justify-center w-10 h-10">
          <Menu v-if="!showLinks" class="h-8 w-8 text-foreground" />
          <X v-else class="h-8 w-8 text-foreground" />
        </button>
      </template>

      <!-- Nav body -->
      <div :class="effectiveCollapsed ? '' : 'px-2'">
        <NavLink text="Home" icon="Home" link="/" :icon-only="effectiveCollapsed" />
      </div>

      <Popover :open="showNotificationsMobile" @update:open="
        (v) => {
          if (v) {
            showNotificationsMobile = true
            getNotifications(0)
          } else {
            closeNotifications()
          }
        }
      ">
        <PopoverTrigger as-child>
          <button type="button" class="w-full flex items-center hover:bg-muted py-3 cursor-pointer h-14 pl-4 lg:hidden">
            <div class="relative">
              <Bell class="w-6 h-6 text-foreground" :stroke-width="2" />
              <span v-if="notificationCount > 0"
                class="absolute -top-2.5 -right-2 bg-red-600 text-white text-[12px] font-bold rounded-full px-1.5 flex items-center justify-center text-center whitespace-nowrap">
                {{ notificationCount > 99 ? '99+' : notificationCount }}
              </span>
            </div>
            <p class="text-[15px] pt-0.75 text-foreground font-medium w-auto pl-3 whitespace-wrap self-center">
              Notifications
            </p>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" side="bottom" :side-offset="12" class="w-80 p-0">
          <PopoverArrow :width="14" :height="7" />
          <NotificationsPanel :notifications="notifications" :loading="loadingNotifications"
            :historical="historicalNotifications" :last-page="lastPage" @open="openNotification"
            @load-more="loadMoreNotifications" @toggle-historical="toggleHistorical" />
        </PopoverContent>
      </Popover>

      <template v-for="item in navGroups" :key="item.title || item.text">
        <div v-if="item.type === 'link'" :class="sidebar.collapsed ? '' : 'px-2'">
          <NavLink :text="item.text" :icon="item.icon" :link="item.route" :icon-only="effectiveCollapsed" />
        </div>
        <SidebarGroup v-else-if="item.title !== 'Gift cards' || giftCardsEnabled" :title="item.title" :icon="item.icon"
          :prefix="item.prefix" :links="item.links" :collapsed="effectiveCollapsed" />
      </template>

      <!-- Account footer -->
      <template #footer>
        <DropdownMenu>
          <DropdownMenuTrigger as-child>
            <button type="button"
              class="w-full flex items-center gap-2 rounded-md p-2 hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              :class="sidebar.collapsed ? 'lg:justify-center lg:p-1' : ''">
              <Avatar size="sm">
                <AvatarFallback>{{ initials }}</AvatarFallback>
              </Avatar>
              <div class="grid flex-1 text-left leading-tight min-w-0" :class="sidebar.collapsed ? 'lg:hidden' : ''">
                <span class="text-sm font-medium truncate">{{ userName }}</span>
                <span class="text-xs text-muted-foreground truncate">{{ publisher }}</span>
              </div>
              <ChevronsUpDown class="w-4 h-4 text-muted-foreground shrink-0"
                :class="sidebar.collapsed ? 'lg:hidden' : ''" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="end" :side-offset="8" class="w-56">
            <DropdownMenuLabel class="p-0 font-normal">
              <div class="flex items-center gap-2 px-2 py-1.5 text-left">
                <Avatar size="sm">
                  <AvatarFallback>{{ initials }}</AvatarFallback>
                </Avatar>
                <div class="grid flex-1 leading-tight min-w-0">
                  <span class="text-sm font-medium truncate">{{ userName }}</span>
                  <span class="text-xs text-muted-foreground truncate">{{ publisher }}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem @click="$router.push('/settings')">
                <Cog class="w-4 h-4 mr-2" />Settings
              </DropdownMenuItem>
              <DropdownMenuItem @click="$router.push('/billing')">
                <ReceiptText class="w-4 h-4 mr-2" />Billing
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <component :is="themeIcon" class="w-4 h-4 mr-2" />Theme
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuRadioGroup :model-value="theme.mode" @update:model-value="theme.set($event)">
                  <DropdownMenuRadioItem value="light">
                    <Sun class="w-4 h-4 mr-2" />Light
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="dark">
                    <Moon class="w-4 h-4 mr-2" />Dark
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="system">
                    <Monitor class="w-4 h-4 mr-2" />System
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem @click="signout()">
              <LogOut class="w-4 h-4 mr-2" />Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </template>
    </SidebarShell>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuGroup,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  NavLink,
  SidebarGroup,
  SidebarShell,
} from '@boogi/ui'
import {
  Bell,
  Menu,
  X,
  ChevronsUpDown,
  Cog,
  ReceiptText,
  LogOut,
  Sun,
  Moon,
  Monitor,
} from 'lucide-vue-next'
import NotificationsPanel from '@/components/navbars/NotificationsPanel.vue'
import { sidebarState } from '@/stores/sidebar'
import { themeState } from '@/stores/theme'
export default {
  name: 'SideBar',
  components: {
    NavLink,
    SidebarGroup,
    SidebarShell,
    NotificationsPanel,
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogCancel,
    AlertDialogAction,
    Avatar,
    AvatarFallback,
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuGroup,
    DropdownMenuSub,
    DropdownMenuSubTrigger,
    DropdownMenuSubContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverArrow,
    Bell,
    Menu,
    X,
    ChevronsUpDown,
    Cog,
    ReceiptText,
    LogOut,
    Sun,
    Moon,
    Monitor,
  },
  data() {
    return {
      sidebar: sidebarState,
      theme: themeState,
      // Collapse is a desktop-only affordance. Below `lg` the sidebar becomes a
      // hamburger dropdown, where an icon-only nav has no room to make sense and
      // looks broken — so we only honour the collapsed state at lg and up.
      isDesktop: typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
      publisher: localStorage.getItem('publisher'),
      userName:
        (localStorage.getItem('firstName') || '') + ' ' + (localStorage.getItem('lastName') || ''),
      openDropdown: '',
      showLinks: false,
      showConfirmModal: false,
      showNotificationsSidebar: false,
      showNotificationsMobile: false,
      loading: false,
      credits: undefined,
      loyaltyOpen: false,
      marketingOpen: false,
      caOpen: false,
      rewardemailOpen: false,
      notifications: [],
      historicalNotifications: false,
      loadingNotifications: false,
      notificationCount: 0,
      page: 0,
      lastPage: false,
      // Gift cards are opt-in per publisher (GIFTCARDS product). Default false so the nav
      // never flashes the group before entitlement is known.
      giftCardsEnabled: false,
      navGroups: [
        {
          title: 'Cashback program',
          icon: 'Layers',
          prefix: '/cashback-program',
          links: [
            {
              text: 'Your program',
              icon: 'BadgePercent',
              route: '/cashback-program/program-offers',
            },
            { text: 'Offers catalogue', icon: 'Book', route: '/cashback-program/offer-catalogue' },
            {
              text: 'Custom offers',
              icon: 'ReceiptText',
              route: '/cashback-program/custom-offers',
            },
            { text: 'Settlements', icon: 'HandCoins', route: '/cashback-program/settlements' },
            { text: 'Cashback ledger', icon: 'BookOpenText', route: '/cashback-program/ledger' },
          ],
        },
        {
          title: 'Perks and rewards',
          icon: 'Gift',
          prefix: '/perks-and-rewards',
          links: [
            { text: 'Perks', icon: 'ShoppingCart', route: '/perks-and-rewards/product-catalogue' },
            { text: 'Perks inventory', icon: 'Box', route: '/perks-and-rewards/inventory' },
            {
              text: 'Perks orders',
              icon: 'ShoppingBasket',
              route: '/perks-and-rewards/product-orders',
            },
          ],
        },
        {
          title: 'Gift cards',
          icon: 'CreditCard',
          prefix: '/gift-cards',
          links: [
            {
              text: 'Gift cards',
              icon: 'Store',
              route: '/gift-cards',
            },
            {
              text: 'Orders',
              icon: 'ShoppingBasket',
              route: '/gift-cards/orders',
            },
          ],
        },
        {
          title: 'Enrolled users',
          icon: 'Users',
          prefix: '/enrolled-users',
          links: [
            { text: 'Users', icon: 'User', route: '/enrolled-users/users' },
            { text: 'User segments', icon: 'UsersRound', route: '/enrolled-users/user-segments' },
          ],
        },
        { type: 'link', text: 'Boogi Intelligence', icon: 'BrainCircuit', route: '/intelligence' },

        {
          type: 'link',
          text: 'Transaction processing',
          icon: 'Sparkles',
          route: '/transaction-processing',
        },



        { type: 'link', text: 'Support tickets', icon: 'HandHelping', route: '/support' },
        {
          title: 'Whitelabel',
          icon: 'Palette',
          prefix: '/whitelabel',
          links: [
            { text: 'Sites', icon: 'LayoutGrid', route: '/whitelabel/sites' },
            { text: 'Themes', icon: 'Brush', route: '/whitelabel/themes' },
          ],
        },
        {
          title: 'Developers',
          icon: 'Code',
          prefix: '/developers',
          // Ordered by how often they are used, not alphabetically.
          links: [
            { text: 'Webhooks', icon: 'Webhook', route: '/developers/webhooks' },
            { text: 'API keys', icon: 'KeySquare', route: '/developers/api-keys' },
            { text: 'Catalogue SDK', icon: 'AppWindow', route: '/developers/sdk-manager' },
            { text: 'Playground', icon: 'Play', route: '/developers/playground' },
          ],
        },
      ],
    }
  },

  created() {
    this.getNotificationCount()
    this.getGiftCardEntitlement()
  },

  mounted() {
    this.desktopMq = window.matchMedia('(min-width: 1024px)')
    this.onBreakpointChange = (e) => {
      this.isDesktop = e.matches
    }
    this.desktopMq.addEventListener('change', this.onBreakpointChange)
  },

  beforeUnmount() {
    this.desktopMq?.removeEventListener('change', this.onBreakpointChange)
  },

  watch: {
    '$route.path'(newPath) {
      this.currentPage = newPath
      this.getNotificationCount()
    },
  },

  methods: {
    openUrl(url) {
      window.open(url, '_blank')
    },

    closeNotifications() {
      this.showNotificationsSidebar = false
      this.showNotificationsMobile = false
      this.historicalNotifications = false
      this.page = 0
      this.notifications = []
      this.getNotificationCount()
    },

    async openNotification(notification) {
      notification.interactedWith = true
      await this.markAsSeen(notification)
      // Refresh the unread count so the badge reflects the notification being hidden.
      this.getNotificationCount()
      // Remove it from the live (non-historical) list so it disappears immediately.
      if (!this.historicalNotifications) {
        this.notifications = this.notifications.filter((n) => n.id !== notification.id)
      }
      this.navigateToNotification(notification.url)
    },

    navigateToNotification(url) {
      if (!url) return
      // Notifications link to an offer via an absolute dashboard URL
      // (https://dashboard.boogi.io/retailer/.../offer/...). Route those
      // INTERNALLY by stripping the origin, so the link works in dev/sandbox
      // too — otherwise window.open sent the user to prod in a new tab and the
      // in-app navigation never happened. Genuinely external links still open
      // in a new tab.
      let path = url
      const abs = url.match(/^https?:\/\/[^/]+(\/.*)$/i)
      if (abs && /(^|\/\/)([^/]*\.)?boogi\.io|localhost/i.test(url)) {
        path = abs[1]
      } else if (/^https?:\/\//i.test(url)) {
        window.open(url, '_blank', 'noopener')
        return
      }
      if (!path.startsWith('/')) path = `/${path}`
      this.closeNotifications()
      if (this.$route.fullPath !== path) {
        this.$router.push(path).catch(() => { })
      }
    },

    loadMoreNotifications() {
      this.page = this.page + 1
      if (this.historicalNotifications) {
        this.getHistoricalNotifications(this.page)
      } else {
        this.getNotifications(this.page)
      }
    },

    toggleHistorical() {
      this.historicalNotifications = !this.historicalNotifications
      this.notifications = []
      this.page = 0

      if (this.historicalNotifications) {
        this.getHistoricalNotifications(0)
      } else {
        this.getNotifications(0)
      }
    },

    async getHistoricalNotifications(page) {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/notifications/historic?page=${page}&size=10`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.notifications = [...this.notifications, ...object.content]
          this.lastPage = object.last
        }
      } catch (error) {
        console.error(error)
        return
      }
    },

    async getNotifications(page) {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/notifications?page=${page}&size=10`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.notifications = [...this.notifications, ...object.content]
          this.lastPage = object.last
        }
      } catch (error) {
        console.error(error)
        return
      }
    },

    // Gift cards are opt-in per publisher. The nav group is hidden unless the publisher
    // holds the GIFTCARDS product; the backend enforces this independently, so this is
    // presentation only.
    async getGiftCardEntitlement() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        const response = await this.$apiFetch(`${apiUrl}/private/api/client/publisher`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })

        if (response.ok) {
          const publisher = await response.json()
          this.giftCardsEnabled =
            Array.isArray(publisher.products) && publisher.products.includes('GIFTCARDS')
        }
      } catch (error) {
        // Fail closed: leave the group hidden.
      }
    },

    async getNotificationCount() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true
        const response = await this.$apiFetch(`${apiUrl}/private/api/client/notifications/count`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })

        if (response.ok) {
          var object = await response.json()
          this.notificationCount = object.count
        }
      } catch (error) {
        console.error(error)
        return
      }
    },

    async markAsSeen(notification) {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true
        await this.$apiFetch(`${apiUrl}/private/api/client/notifications/${notification.id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })
      } catch (error) {
        console.error(error)
        return
      }
    },

    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject, { setZone: true }).toLocaleString(DateTime.DATE_MED)
    },
    daysRemaining(dateObject) {
      const date1 = DateTime.fromMillis(dateObject, { setZone: true })
      const date2 = DateTime.now()
      this.diff = date1.diff(date2, ['years', 'months', 'days', 'hours'])
      var x = this.diff.toObject()
      return x.days
    },

    closeModal() {
      this.showConfirmModal = false
    },
    handleSignOutConfirm() {
      this.showConfirmModal = true
    },
    navigateToHelp() { },
    handleExpand(value) {
      if (value == 'marketing') {
        this.$router.push('/automated-marketing').catch(() => { })
      }
      if (value == 'communication') {
        this.$router.push('/customer-acquisition').catch(() => { })
      }
      if (value == 'listing') {
        {
          this.$router.push('/listings').catch(() => { })
        }
      }
      if (value == 'customers') {
        {
          this.$router.push('/customers').catch(() => { })
        }
      }
      if (value == 'venue') {
        {
          this.$router.push('/venue').catch(() => { })
        }
      }
      if (value == 'offersAndBrand') {
        {
          this.$router.push('/offers-and-brand-deals').catch(() => { })
        }
      }
      this.openDropdown = value
    },
    expandDropdown() {
      if (this.showLinks === true) this.handleExpand('')
      this.showLinks = !this.showLinks
    },
    async signout() {
      // Revoke the token server-side BEFORE clearing storage. Clearing localStorage alone only
      // signs the user out of this browser — the idToken stays valid until its natural expiry, so
      // a copy of it could still be replayed. The endpoint deny-lists the token and signs the
      // Cognito session out globally.
      await this.revokeSession()
      localStorage.clear()
      this.$router.push('/auth/sign-in')
    },
    async revokeSession() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
        if (!token) return
        await this.$apiFetch(`${apiUrl}/private/api/client/user/logout`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })
      } catch (error) {
        // Never block sign-out on a failed revoke — the user must always be able to leave. The
        // token still expires on its own; this is best-effort hardening.
        console.error(error)
      }
    },
  },
  computed: {
    // The sidebar's stored collapsed state only takes effect on desktop widths.
    effectiveCollapsed() {
      return this.sidebar.collapsed && this.isDesktop
    },
    themeIcon() {
      return { light: Sun, dark: Moon, system: Monitor }[this.theme.mode] || Monitor
    },
    initials() {
      const f = (localStorage.getItem('firstName') || '').trim()
      const l = (localStorage.getItem('lastName') || '').trim()
      const i = ((f[0] || '') + (l[0] || '')).toUpperCase()
      return i || 'U'
    },
  },
}
</script>
